import React, { useContext, useState, useMemo } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { AuthContext, useAuth } from '../../Context/AuthContext';
import { AccountCircle, SettingsOutlined as SettingsOutlinedIcon, ExitToApp, KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Person from '@mui/icons-material/Person';
import Email from '@mui/icons-material/Email';
import Badge from '@mui/icons-material/Badge';

export default function ProfileMenu() {
    const { isTrainer, isDirector, isParticipant ,isSuperAdmin, } = useContext(AuthContext);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout, rememberedIdentities, switchAccount } = useAuth();
  const navigate = useNavigate();

  const otherIdentities = useMemo(() => {
    const list = Array.isArray(rememberedIdentities) ? rememberedIdentities : [];
    return list.filter((i) => i?.email && i.email !== user?.email);
  }, [rememberedIdentities, user]);
  
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    // Determine profile page based on user role
    if (user?.role === 'client') {
      navigate('/client/profil');
    } else if (user?.role === 'intervenant') {
      navigate('/intervenant/profil');
    } else {
      navigate('/profile');
    }
  };

  const handleLogout =  async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  const handleSettings = () => {
    handleClose();
    navigate('/settings');
  };

  const handleSwitchAccount = async (email) => {
    handleClose();
    try {
      await switchAccount(email); // sets PREFILL_EMAIL and revokes session
    } finally {
      navigate('/login'); // go to login with prefilled email if any
    }
  };

  return (
    <>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
        sx={{ borderRadius: '8px', p: 0.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {user?.photo ? (
            <Avatar 
              src={user.photo} 
              alt={user.nom || 'User'} 
              sx={{ width: 32, height: 32, marginRight: 1 }}
            />
          ) : (
            <AccountCircle sx={{ marginRight: 1 }} />
          )}
          {user?.nom && (
            <Typography variant="body1" sx={{ display: { xs: 'none', md: 'block' }, color: 'inherit', marginRight: 0.5 }}>
              {user.nom}
            </Typography>
          )}
          <KeyboardArrowDownIcon sx={{ fontSize: 22, color: 'inherit' }} />
        </Box>
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {user && (
          <>
          <MenuItem onClick={(e) => e.preventDefault()} sx={{ pointerEvents: 'none' }} >
          <Person fontSize="small" sx={{ mr: 1 }} />

            <Typography variant="body2" >
              {user.nom || user.email || 'User'}
            </Typography>
          </MenuItem>
          <MenuItem onClick={(e) => e.preventDefault()} sx={{ pointerEvents: 'none' }}>
          <Badge fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">
              {typeof user.role === 'object' ? (user.role?.type) : user.role }
            </Typography>
          </MenuItem>
          </>
          
        )}
        <Divider />

        {otherIdentities.length > 0 && (
          <>
            <MenuItem disabled>
              <ListItemText primary="Switch account" />
            </MenuItem>
            {otherIdentities.map((acc) => (
              <MenuItem key={acc.email} onClick={() => handleSwitchAccount(acc.email)}>
                <ListItemIcon>
                  <Avatar src={acc.avatarUrl || undefined} sx={{ width: 24, height: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">{acc.displayName || acc.email}</Typography>
                      {acc.roleType && (
                        <Chip
                          label={acc.roleType}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={acc.email}
                />
              </MenuItem>
            ))}
            <MenuItem onClick={() => handleSwitchAccount(undefined)}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Use another account" />
            </MenuItem>
            <Divider />
          </>
        )}

        <MenuItem onClick={handleProfile}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Bio" />
        </MenuItem>
        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <SettingsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>
    </>
  );
}